import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import React, { KeyboardEvent, RefObject, useRef, useState } from 'react';
import { IconType, useFullTheme } from '~/design';
import { ChevronDownOutlineIcon, NewspaperStarOutlineIcon } from '@applyboard/ui-icons';
import { Text } from '../Text';
import { assert, useResponsive } from '~/utils';
import { flushSync } from 'react-dom';
import { getHeadingVariant } from '../Markdown';
import { IconWrapper } from '~/internal';
import { useSlate } from 'slate-react';
import { Editor, Transforms, Element as SlateElement } from 'slate';
import { getBlockOfType, isBlockActive } from './RichTextEditor.utils';
import { HelpText } from '../HelpText';
import { useContextOrThrow } from '~/utils/useContextOrThrow';
import { HeadingsConfig } from './RichTextEditor.types';

const AVAILABLE_FONTS = [
  { label: 'Heading 1', level: 1, value: 'h1' },
  { label: 'Heading 2', level: 2, value: 'h2' },
  { label: 'Heading 3', level: 3, value: 'h3' },
  { label: 'Heading 4', level: 4, value: 'h4' },
  { label: 'Heading 5', level: 5, value: 'h5' },
  { label: 'Heading 6', level: 6, value: 'h6' },
  { label: 'Body', level: 0, value: 'p' },
] as const;

type FontVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';

export const ToolbarContext = React.createContext<{
  disabled?: boolean;
  toolbarRef: RefObject<HTMLDivElement>;
} | null>(null);

function useToolbar() {
  const context = useContextOrThrow(ToolbarContext);

  const getToolbarButtons = () =>
    Array.from(context.toolbarRef.current?.querySelectorAll('button') ?? []);

  return {
    disabled: !!context.disabled,
    getTabIndex: (ref: HTMLButtonElement | null) => {
      const index = getToolbarButtons().findIndex(b => b === ref);
      return index === 0 ? 0 : -1;
    },
    moveLeft: (e: KeyboardEvent) => {
      const toolbarButtons = getToolbarButtons();
      const index = toolbarButtons.findIndex(b => b === e.target);
      if (index > 0) {
        e.preventDefault();
        toolbarButtons[index - 1].focus();
      }
    },
    moveRight: (e: KeyboardEvent) => {
      const toolbarButtons = getToolbarButtons();
      const index = toolbarButtons.findIndex(b => b === e.target);
      if (index < toolbarButtons.length - 1) {
        e.preventDefault();
        toolbarButtons[index + 1].focus();
      }
    },
  };
}

export function Alerts() {
  const { colors, elevations, spacing } = useFullTheme();
  const [isOpen, setIsOpen] = React.useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const toolbar = useToolbar();
  const editor = useSlate();

  const importanceOptions = [
    {
      label: 'Note',
      importance: 'note',
      intent: 'primary',
    },
    {
      label: 'Tip',
      importance: 'tip',
      intent: 'positive',
    },
    {
      label: 'Warning',
      importance: 'warning',
      intent: 'warning',
    },
    {
      label: 'Caution',
      importance: 'caution',
      intent: 'negative',
    },
  ] as const;

  return (
    <RadixDropdownMenu.Root
      onOpenChange={val => {
        if (toolbar.disabled) {
          return;
        }
        flushSync(() => {
          setIsOpen(val);
        });
        if (val) {
          menuRef.current?.querySelectorAll('li').item(0)?.focus();
        }
      }}
      open={isOpen}
    >
      <RadixDropdownMenu.Trigger asChild>
        <IconButton
          aria-label="Add alert"
          icon={NewspaperStarOutlineIcon}
          isActive={isBlockActive(
            editor,
            node => node.type === 'blockquote' && typeof node.importance === 'string',
          )}
        />
      </RadixDropdownMenu.Trigger>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content asChild sideOffset={8}>
          <div
            css={{
              backgroundColor: colors.bgRegular,
              borderRadius: '4px',
              boxShadow: elevations.z1,
              padding: spacing[2],
            }}
            ref={menuRef}
          >
            <ul
              css={{
                display: 'flex',
                gap: spacing['2px'],
                flexDirection: 'column',
                margin: 0,
                padding: 0,
              }}
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
              role="menu"
            >
              {importanceOptions.map(({ importance, label, intent }, i) => {
                return (
                  <ListItem
                    key={i}
                    index={i}
                    items={importanceOptions}
                    menuRef={menuRef}
                    value={importance}
                    isSelected={isBlockActive(
                      editor,
                      node => node.type === 'blockquote' && node.importance === importance,
                    )}
                    onClick={() => {
                      setIsOpen(false);
                      if (!editor.selection) return;

                      const blockquote = getBlockOfType(editor, 'blockquote');

                      if (blockquote) {
                        assert('type' in blockquote && blockquote.type === 'blockquote');

                        const oldImportance = blockquote.importance;

                        if (!oldImportance || oldImportance === importance) {
                          Transforms.unwrapNodes(editor, {
                            match: n =>
                              !Editor.isEditor(n) &&
                              SlateElement.isElement(n) &&
                              n.type === 'blockquote',
                          });
                        } else {
                          Transforms.setNodes(
                            editor,
                            {
                              importance,
                            },
                            {
                              match: n =>
                                !Editor.isEditor(n) &&
                                SlateElement.isElement(n) &&
                                n.type === 'blockquote',
                            },
                          );
                        }
                      } else {
                        Transforms.wrapNodes(
                          editor,
                          {
                            type: 'blockquote',
                            importance,
                            children: [],
                          },
                          {
                            split: false,
                            mode: 'highest',
                          },
                        );
                      }
                    }}
                  >
                    <HelpText showIcon intent={intent}>
                      {label}
                    </HelpText>
                  </ListItem>
                );
              })}
            </ul>
          </div>
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
}

export function Fonts({
  onClick,
  value,
  headingsConfig,
}: {
  onClick: (value: FontVariant) => void;
  value: string | null;
  headingsConfig: HeadingsConfig;
}) {
  const fonts = AVAILABLE_FONTS.filter(({ level }) => level <= headingsConfig.maxLevel);
  const { colors, elevations } = useFullTheme();
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedOption = AVAILABLE_FONTS.find(f => f.value === (value ?? 'bodyM'));
  const menuRef = useRef<HTMLDivElement>(null);
  const selectedOptionLabel = selectedOption?.label ?? 'Body';
  const toolbar = useToolbar();

  return (
    <RadixDropdownMenu.Root
      onOpenChange={val => {
        if (toolbar.disabled) {
          return;
        }
        flushSync(() => {
          setIsOpen(val);
        });
        if (val) {
          menuRef.current?.querySelectorAll('li').item(0)?.focus();
        }
      }}
      open={isOpen}
    >
      <DropdownButtonTrigger aria-label={`Change font style (${selectedOptionLabel})`}>
        {selectedOptionLabel}
      </DropdownButtonTrigger>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content asChild sideOffset={8}>
          <div
            css={{
              backgroundColor: colors.bgRegular,
              borderRadius: '4px',
              boxShadow: elevations.z1,
              maxHeight: 400,
              overflowY: 'auto',
            }}
            ref={menuRef}
          >
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */}
            <ul css={{ margin: 0, padding: 0 }} role="menu">
              {fonts.map((child, i) => {
                const isSelected = (selectedOption?.value ?? 'bodyM') === child.value;
                const headingStyle =
                  child.level !== 0 ? headingsConfig.styles?.[`h${child.level}`] : undefined;
                return (
                  <ListItem
                    index={i}
                    isSelected={isSelected}
                    items={fonts}
                    key={child.value}
                    menuRef={menuRef}
                    onClick={value => {
                      onClick(value as FontVariant);
                      setIsOpen(false);
                    }}
                    value={child.value}
                  >
                    <Text
                      variant={headingStyle?.variant || getHeadingVariant(child.level)}
                      contrast={headingStyle?.contrast}
                    >
                      <span style={{ fontWeight: headingStyle?.bold ? 700 : undefined }}>
                        {child.label}
                      </span>
                    </Text>
                  </ListItem>
                );
              })}
            </ul>
          </div>
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
}

function ListItem({
  children,
  index,
  isSelected = false,
  items,
  menuRef,
  onClick,
  value,
}: {
  children: React.ReactNode;
  index: number;
  isSelected?: boolean;
  items: readonly unknown[];
  menuRef: RefObject<HTMLDivElement>;
  onClick: (value: unknown) => void;
  value: unknown;
}) {
  const { colors, spacing } = useFullTheme();
  return (
    <li
      aria-checked={isSelected ? 'true' : 'false'}
      css={{
        backgroundColor: isSelected ? colors.bgPrimaryHighlight : undefined,
        border: 0,
        cursor: 'pointer',
        listStyleType: 'none',
        padding: spacing[2],
        width: '100%',
        '&:hover': {
          backgroundColor: isSelected ? colors.bgPrimaryHighlightHover : colors.bgRegularHover,
        },
      }}
      onMouseDown={e => {
        e.preventDefault();
      }}
      onClick={() => {
        onClick(value);
      }}
      onKeyDown={e => {
        if ([' ', 'Enter'].includes(e.key)) {
          onClick(value);
        } else {
          handleMenuItemButtonKeyDown(
            menuRef,
            e.key === 'ArrowDown' ? 'next' : e.key === 'ArrowUp' ? 'prev' : null,
            index,
            items,
          );
        }
      }}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role="menuitemradio"
      tabIndex={-1}
    >
      {children}
    </li>
  );
}

export const IconButton = React.forwardRef<
  HTMLButtonElement,
  {
    'aria-label': string;
    disabled?: boolean;
    icon?: IconType;
    isActive?: boolean;
    onClick?:
      | ((e: React.MouseEvent<HTMLButtonElement>) => void)
      | ((e: React.KeyboardEvent<HTMLButtonElement>) => void);
  }
>(function IconButton(
  { 'aria-label': ariaLabel, disabled = false, icon: Icon, isActive, onClick, ...props },
  ref,
) {
  const { borderRadiuses, colors } = useFullTheme();
  const toolbar = useToolbar();
  const [tabIndex, setTabIndex] = useState(-1);
  const isDisabled = toolbar.disabled || disabled;

  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={isActive ? 'true' : 'false'}
      css={{
        alignItems: 'center',
        backgroundColor: isActive ? colors.actionSecondary : 'transparent',
        border: 0,
        borderRadius: borderRadiuses.control,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        height: '28px',
        justifyContent: 'center',
        margin: 0,
        padding: 0,
        width: '28px',
      }}
      disabled={isDisabled}
      onMouseDown={e => e.preventDefault()}
      onClick={e => {
        // @ts-expect-error TS expects a union or something :\
        onClick?.(e);
      }}
      {...props}
      onKeyDown={e => {
        if ([' ', 'Enter'].includes(e.key)) {
          // @ts-expect-error TS expects a MouseEvent
          onClick?.(e);
        } else if (e.key === 'ArrowRight') {
          toolbar.moveRight(e);
        } else if (e.key === 'ArrowLeft') {
          toolbar.moveLeft(e);
        }
        // @ts-expect-error This will be valid for the dropdown menus
        props.onKeyDown?.(e); // eslint-disable-line react/prop-types
      }}
      ref={el => {
        if (typeof ref === 'function') {
          ref?.(el);
        } else if (ref) {
          ref.current = el;
        }
        if (el) {
          setTabIndex(toolbar.getTabIndex(el));
        }
      }}
      tabIndex={tabIndex}
      type="button"
    >
      {Icon ? (
        <IconWrapper
          contrast={isActive ? 'inverted' : 'high'}
          disabled={isDisabled}
          intent="secondary"
          selector="button"
        >
          <Icon />
        </IconWrapper>
      ) : (
        ariaLabel
      )}
    </button>
  );
});

function DropdownButtonTrigger({
  'aria-label': ariaLabel,
  children,
  ...props
}: {
  'aria-label': string;
  children: React.ReactNode;
}) {
  const { colors, spacing, typography } = useFullTheme();
  const responsive = useResponsive();
  const toolbar = useToolbar();
  const [tabIndex, setTabIndex] = useState(-1);

  return (
    <RadixDropdownMenu.Trigger asChild>
      <button
        aria-label={ariaLabel}
        css={[
          responsive.css(typography.bodyM),
          {
            alignItems: 'center',
            backgroundColor: 'transparent',
            border: 0,
            color: toolbar.disabled ? colors.textInactive : colors.textSecondaryStark,
            cursor: toolbar.disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            gap: spacing[1],
            height: '28px',
            margin: 0,
            paddingBlock: 0,
            paddingLeft: spacing[2],
            paddingRight: spacing[1],
          },
        ]}
        disabled={toolbar.disabled}
        ref={el => {
          if (el) {
            setTabIndex(toolbar.getTabIndex(el));
          }
        }}
        tabIndex={tabIndex}
        {...props}
        onMouseDown={e => e.preventDefault()}
        onKeyDown={e => {
          if (e.key === 'ArrowRight') {
            toolbar.moveRight(e);
          } else if (e.key === 'ArrowLeft') {
            toolbar.moveLeft(e);
          }
        }}
        type="button"
      >
        {children}
        <IconWrapper
          contrast="high"
          disabled={toolbar.disabled}
          intent="secondary"
          selector="button"
        >
          <ChevronDownOutlineIcon />
        </IconWrapper>
      </button>
    </RadixDropdownMenu.Trigger>
  );
}

function handleMenuItemButtonKeyDown(
  menuRef: RefObject<HTMLDivElement>,
  direction: 'next' | 'prev' | null,
  index: number,
  optsArray: readonly unknown[],
) {
  const btns = menuRef.current?.querySelectorAll('li');
  if (direction === 'next') {
    if (index < optsArray.length - 1) {
      btns?.item(index + 1).focus();
    }
  } else if (direction === 'prev') {
    if (index > 0) {
      btns?.item(index - 1).focus();
    }
  }
}
